import { rfidTagApi } from "#/api/rfidTagApi";
import { sitesApi } from "#/api/sitesApi";
import { vehicleApi } from "#/api/vehicleApi";
import { vehicleModelApi } from "#/api/vehicleModelApi";
import ActionButton from "#/components/buttons/ActionButton";
import { showErrorNotification, showSuccessNotification } from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import type {
  CreateVehiclePayload,
  UpdateVehiclePayload,
  Vehicle,
  VehicleDriver,
} from "#/types/vehicle";
import { Accordion, Button, Select, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import DriversAccordion from "./components/DriversAccordion";

/** Rows the backend would reject: empty name, end not after start, no weekday. */
const invalidDriver = (driver: VehicleDriver): boolean =>
  driver.name.trim() === "" ||
  driver.endTime <= driver.startTime ||
  driver.workingDays.length === 0;

/** The editable part of a vehicle, as the form holds it. */
const toFormValues = (vehicle: Vehicle): CreateVehiclePayload => ({
  rfid_id: vehicle.rfid_tag?.id ?? 0,
  name: vehicle.name ?? "",
  maintenance: vehicle.maintenance ?? undefined,
  status: vehicle.status ?? undefined,
  vehicle_model_id: vehicle.vehicle_model?.id ?? 0,
  site_id: vehicle.site?.id ?? undefined,
  customize_columns: { ...(vehicle.customize_columns ?? {}) },
  drivers: (vehicle.drivers ?? []).map((driver) => ({
    id: driver.id,
    name: driver.name,
    startTime: driver.startTime,
    endTime: driver.endTime,
    workingDays: [...driver.workingDays],
  })),
});

const VehicleDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWrite } = usePermission();
  const queryClient = useQueryClient();

  const form = useForm<CreateVehiclePayload>({
    initialValues: {
      rfid_id: 0,
      name: "",
      maintenance: undefined,
      status: undefined,
      vehicle_model_id: 0,
      site_id: undefined,
      customize_columns: {},
      drivers: [],
    },
    validate: {
      // A tag is required to create a vehicle. An existing vehicle without one
      // (imported or seeded) must still be editable, so on update it is optional.
      rfid_id: (value) =>
        id !== "new" || (value && value > 0) ? null : t("vehicles.validation.rfidRequired"),
      name: (value) => (value ? null : t("vehicles.validation.nameRequired")),
      vehicle_model_id: (value) =>
        value && value > 0 ? null : t("vehicles.validation.vehicleModelRequired"),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    if ((values.drivers ?? []).some(invalidDriver)) {
      showErrorNotification({ message: t("vehicles.drivers.invalid") });
      return;
    }
    const payload: CreateVehiclePayload = {
      ...values,
      drivers: (values.drivers ?? []).map((driver) => ({
        ...(driver.id ? { id: driver.id } : {}),
        name: driver.name.trim(),
        startTime: driver.startTime,
        endTime: driver.endTime,
        workingDays: driver.workingDays,
      })),
    };

    vehicleMutation.mutate(payload);
  };

  const vehicleMutation = useMutation({
    mutationFn: async (payload: CreateVehiclePayload) => {
      if (id !== "new") {
        // 0 means "no tag chosen": never send it, the backend would try to link tag 0.
        const { rfid_id, ...rest } = payload;
        const body: UpdateVehiclePayload = rfid_id > 0 ? payload : rest;
        return await vehicleApi.update(Number(id), body);
      } else {
        return await vehicleApi.create(payload);
      }
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: id === "new" ? t("vehicles.createdSuccess") : t("vehicles.updatedSuccess"),
      });
      if (id === "new") {
        void navigate(`/fleet/vehicles/${data.data.id}`, { replace: true });
      } else {
        // The form now matches what is stored: make that its clean baseline so
        // Save disables and a later refetch has nothing to overwrite.
        const saved = toFormValues(data.data);
        form.setInitialValues(saved);
        form.setValues(saved);
        form.resetDirty(saved);
        // Refetch so the page (e.g. the current RFID tag shown in the tag
        // dropdown) reflects what was saved, not the pre-save fetch.
        void queryClient.invalidateQueries({ queryKey: ["vehicleDetails", id] });
      }
    },
    onError: (error) => {
      logger.error("VehicleDetail save error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const { data: vehicle } = useQuery({
    queryKey: ["vehicleDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid vehicleId");

      const response = await vehicleApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch vehicle");
      return response.data;
    },
  });

  // Load the fetched vehicle into the form, but never over unsaved edits: React
  // Query refetches on window focus, and that used to wipe what the user was
  // typing. Each fetched object is applied once; while the form is dirty it is
  // left alone and picked up after the next save makes the form clean again.
  const appliedVehicleRef = useRef<Vehicle | null>(null);
  useEffect(() => {
    if (!vehicle || appliedVehicleRef.current === vehicle || form.isDirty()) return;
    const values = toFormValues(vehicle);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedVehicleRef.current = vehicle;
  }, [vehicle, form]);

  useUnsavedChangesWarning(form.isDirty());

  const { data: { vehicleModels = [] } = {} } = useQuery({
    queryKey: ["vehicle-models-all"],
    queryFn: async () => {
      const response = await vehicleModelApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch vehicle models");
      }
      return {
        vehicleModels: response.data.data,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites-all"],
    queryFn: async () => {
      const response = await sitesApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const [rfidTagSearch, setRfidTagSearch] = useState("");
  const [debouncedRfidTagSearch] = useDebouncedValue(rfidTagSearch, 400);

  const { data: { rfidTags = [] } = {}, isLoading: rfidTagsLoading } = useQuery({
    queryKey: ["rfid-tags-search", debouncedRfidTagSearch],
    queryFn: async () => {
      const response = await rfidTagApi.getAll({
        linked: false,
        limit: 20,
        search: debouncedRfidTagSearch,
      });
      return { rfidTags: response.data.data || [] };
    },
    enabled: debouncedRfidTagSearch.length >= 2,
  });

  const selectedVehicleModel = vehicleModels.find(
    (model) => model.id === form.values.vehicle_model_id
  );
  const statusOptions = useMemo(
    () =>
      (selectedVehicleModel?.allowed_statuses ?? []).map((status) => ({
        value: status,
        label: sanitizeItemStatusLabel(status),
      })),
    [selectedVehicleModel]
  );

  // When editing, always include the vehicle's current tag in options
  const rfidTagOptions = useMemo(() => {
    const options = rfidTags.map((tag) => ({
      label: tag.rfid,
      value: tag.id.toString(),
    }));
    if (id !== "new" && vehicle?.rfid_tag) {
      const currentTag = vehicle.rfid_tag;
      if (!rfidTags.some((t) => t.id === currentTag.id)) {
        options.unshift({ label: currentTag.rfid, value: currentTag.id.toString() });
      }
    }
    return options;
  }, [rfidTags, id, vehicle?.rfid_tag]);

  return (
    <DetailPageLayout
      title={id !== "new" ? t("vehicles.vehicleDetails") : t("vehicles.createVehicle")}
      navRightSection={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={vehicleMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Accordion
            variant="separated"
            defaultValue={["basic", "drivers", "customize_columns"]}
            multiple
          >
            <Accordion.Item value="basic">
              <Accordion.Control>{t("common.basicInformation")}</Accordion.Control>
              <Accordion.Panel>
                <div className="flex w-full flex-col gap-4">
                  <div className="grid w-full grid-cols-2 gap-4">
                    <TextInput
                      label={t("vehicles.form.name")}
                      withAsterisk
                      disabled={!canWrite()}
                      placeholder={t("vehicles.form.namePlaceholder")}
                      {...form.getInputProps("name")}
                    />

                    <Select
                      label={t("vehicles.form.rfid")}
                      withAsterisk
                      placeholder="Type to search RFID tags"
                      searchable
                      searchValue={rfidTagSearch}
                      onSearchChange={setRfidTagSearch}
                      disabled={!canWrite()}
                      data={rfidTagOptions}
                      value={form.values.rfid_id ? form.values.rfid_id.toString() : null}
                      error={form.errors.rfid_id}
                      onChange={(value) => {
                        form.setFieldValue("rfid_id", value ? Number(value) : 0);
                      }}
                      filter={({ options }) => options}
                      nothingFoundMessage={
                        rfidTagSearch.length < 2
                          ? "Type at least 2 characters to search"
                          : rfidTagsLoading
                            ? "Searching..."
                            : "No RFID tags found"
                      }
                    />
                  </div>

                  <div className="grid w-full grid-cols-2 gap-4">
                    <Select
                      label={t("vehicles.form.vehicleModel")}
                      withAsterisk
                      placeholder={t("vehicles.form.vehicleModelPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={vehicleModels.map((model) => ({
                        label: `${model.brand} ${model.model} (${model.name})`,
                        value: model.id.toString(),
                      }))}
                      value={
                        form.values.vehicle_model_id
                          ? form.values.vehicle_model_id.toString()
                          : null
                      }
                      onChange={(value) => {
                        const modelId = value ? Number(value) : 0;
                        form.setFieldValue("vehicle_model_id", modelId);
                        const newModel = vehicleModels.find((m) => m.id === modelId);
                        const newAllowed = (newModel?.allowed_statuses ?? []) as string[];
                        if (form.values.status && !newAllowed.includes(form.values.status)) {
                          // `null`, not `undefined` — this page's submit has no
                          // normalization layer (`{...values}` direct spread),
                          // so `undefined` would get dropped and silently fail
                          // to clear the now-invalid status.
                          form.setFieldValue("status", null);
                        }
                      }}
                    />

                    <Select
                      label={t("vehicles.form.site")}
                      placeholder={t("vehicles.form.sitePlaceholder")}
                      searchable
                      clearable
                      disabled={!canWrite()}
                      data={sites.map((site) => ({
                        label: site.name,
                        value: site.id.toString(),
                      }))}
                      value={form.values.site_id ? form.values.site_id.toString() : null}
                      onChange={(value) => {
                        // `null` (not `undefined`) so clearing it actually
                        // persists — `undefined` keys get dropped by JSON
                        // before the request reaches the backend.
                        form.setFieldValue("site_id", value ? Number(value) : null);
                      }}
                    />
                  </div>

                  <div className="grid w-full grid-cols-2 gap-4">
                    <DateInput
                      label={t("vehicles.form.maintenanceDate")}
                      placeholder={t("vehicles.form.maintenanceDatePlaceholder")}
                      disabled={!canWrite()}
                      {...form.getInputProps("maintenance")}
                      clearable
                    />

                    <Select
                      label={t("vehicles.form.status")}
                      placeholder={
                        form.values.vehicle_model_id
                          ? t("common.selectStatus")
                          : t("vehicles.form.statusNeedsModel")
                      }
                      searchable
                      clearable
                      disabled={!canWrite() || !form.values.vehicle_model_id}
                      data={statusOptions}
                      {...form.getInputProps("status")}
                    />
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <DriversAccordion
              drivers={form.values.drivers ?? []}
              legacyDriver={vehicle?.assigned_driver ?? null}
              onChange={(drivers) => form.setFieldValue("drivers", drivers)}
            />

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                      disabled={!canWrite()}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <= 0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="flex w-full flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="grid w-full grid-cols-[auto_1fr_1fr_auto] items-center gap-2"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/5 p-2 font-bold text-black/70">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              placeholder={t("common.fieldName")}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return;

                                updated[newKey] = val;
                                form.setFieldValue("customize_columns", updated);
                              }}
                            />

                            <TextInput
                              value={value as string}
                              placeholder={t("common.fieldValue")}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue("customize_columns", updated);
                                }}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default VehicleDetailPage;
